import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormActionButtons from "../Assets/FormActionButtons";
import ImageUpload from "../Assets/ImageUpload";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface DosageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
}

type Multi = { en: string; mk: string; sq: string };

const DosageModal: React.FC<DosageModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [name, setName] = useState<Multi>({ en: "", mk: "", sq: "" });
  const [text, setText] = useState<Multi>({ en: "<p><br></p>", mk: "<p><br></p>", sq: "<p><br></p>" });
  const [image, setImage] = useState<File | null>(null);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    (["en", "mk", "sq"] as FormLang[]).forEach((l) => {
      formData.append(`name[${l}]`, name[l]);
      formData.append(`text[${l}]`, text[l]);
    });
    if (image) {
      formData.append("image", image);
    }
    onSubmit(formData);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setName({ en: "", mk: "", sq: "" });
    setText({ en: "<p><br></p>", mk: "<p><br></p>", sq: "<p><br></p>" });
    setImage(null);
    setActiveLang("en");
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const isTextEmpty = (value: string) =>
    !value || value.trim() === "" || value === "<p><br></p>";

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "link"
  ];

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-2xl font-semibold mb-6 text-brand-800 text-center">
          {t(`forms.createDosage`)}</h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <div className="mb-4">
          <label className="block mb-2">{t('forms.name')} ({langSuffix})</label>
          <input
            type="text"
            value={name[activeLang]}
            onChange={(e) => setField(setName, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2">{t('forms.text')} ({langSuffix})</label>
          <ReactQuill
            key={`text-${activeLang}`}
            value={text[activeLang]}
            onChange={(value) => setField(setText, value)}
            theme="snow"
            className="h-48 mb-4"
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <br />
        <br />

        <ImageUpload onImageSelect={setImage} />

        <FormActionButtons
          onClose={handleCancel}
          onSubmit={handleSubmit}
          edit={false}
          disabled={
            !name.en ||
            !name.mk ||
            isTextEmpty(text.en) ||
            isTextEmpty(text.mk) ||
            !image
          }
        />
      </div>
    </div>
  );
};

export default DosageModal;
